import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useReboModeStore } from '@auw/stores';
import { ComposerSelect, type ComposerSelectOption } from './ComposerSelect';

type McpApprovalMode = ReturnType<typeof useReboModeStore.getState>['mcpApprovalMode'];

/**
 * Store + labels behind the access selector, shared by the inline trigger and
 * the compact popup's access field so both surfaces list the same options.
 */
export function useMcpApprovalModel() {
  const { t } = useTranslation('common');
  const value = useReboModeStore((s) => s.mcpApprovalMode);
  const onChange = useReboModeStore((s) => s.setMcpApprovalMode);
  const options = useMemo<ComposerSelectOption<McpApprovalMode>[]>(() => [
    { id: 'ask_for_approval', label: 'Ask for approval', description: t('mcpApproval.ask_for_approval') },
    { id: 'full_access', label: 'Full access', description: t('mcpApproval.full_access') },
  ], [t]);
  const selectedLabel = options.find((option) => option.id === value)?.label ?? value;
  return { title: t('mcpApproval.label'), value, options, onChange, selectedLabel };
}

interface McpApprovalSelectProps {
  disabled?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Shared by every agent composer, including narrow document panels. */
export function McpApprovalSelect({ disabled = false, open, onOpenChange }: McpApprovalSelectProps) {
  const approval = useMcpApprovalModel();
  return (
    <ComposerSelect
      title={approval.title}
      value={approval.value}
      options={approval.options}
      onChange={approval.onChange}
      open={open}
      onOpenChange={onOpenChange}
      disabled={disabled}
    />
  );
}
